import { createServerFn } from "@tanstack/react-start";
import { codOrderSchema, orderStatusSchema, type CodOrderInput } from "@/lib/orders.schemas";

export type { CodOrderInput };

/**
 * Crée une VRAIE commande Shopify (paiement à la livraison).
 * La commande apparaît immédiatement dans Shopify Admin > Orders,
 * marquée « Pending » / COD.
 */
export const createCodOrder = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => codOrderSchema.parse(input))
  .handler(async ({ data }) => {
    const { shopifyAdminRequest } = await import("@/lib/shopify-admin.server");
    const { newCorrelationId, serverLog } = await import("@/lib/server-log.server");

    const correlationId = newCorrelationId("cod");
    const startedAt = Date.now();

    serverLog("info", "cod_order.received", {
      correlationId,
      city: data.city,
      phone: data.phone,
      lineCount: data.lines.length,
      lines: data.lines,
    });

    const [firstName, ...rest] = data.fullName.split(/\s+/);
    const lastName = rest.join(" ") || firstName;

    const mutation = `
      mutation CreateCodOrder($order: OrderCreateOrderInput!) {
        orderCreate(order: $order, options: { sendReceipt: false, inventoryBehaviour: DECREMENT_IGNORING_POLICY }) {
          order { id name }
          userErrors { field message }
        }
      }
    `;

    const address = {
      firstName,
      lastName,
      address1: data.address,
      city: data.city,
      countryCode: "MA",
      phone: data.phone,
    };

    try {
      const result = await shopifyAdminRequest<{
        orderCreate?: {
          order?: { id: string; name: string } | null;
          userErrors?: Array<{ field: string[] | null; message: string }>;
        };
      }>(
        mutation,
        {
          order: {
            email: null,
            phone: data.phone,
            tags: ["COD", "Storefront Lovable"],
            note: data.note ?? "Commande site — paiement à la livraison (COD)",
            financialStatus: "PENDING",
            billingAddress: address,
            shippingAddress: address,
            lineItems: data.lines.map((l) => ({
              variantId: l.variantId,
              quantity: l.quantity,
            })),
            transactions: [],
          },
        },
        { correlationId, operation: "CreateCodOrder" },
      );

      const errors = result.orderCreate?.userErrors ?? [];
      if (errors.length > 0) {
        serverLog("error", "cod_order.user_errors", {
          correlationId,
          durationMs: Date.now() - startedAt,
          errors,
        });
        throw new Error(errors.map((e) => e.message).join(", "));
      }

      const order = result.orderCreate?.order;
      if (!order) {
        serverLog("error", "cod_order.no_order_returned", {
          correlationId,
          durationMs: Date.now() - startedAt,
        });
        throw new Error("Commande Shopify non créée");
      }

      // Ne confirmer au storefront qu'après avoir relu la commande depuis
      // Shopify Admin. Une réponse orderCreate incomplète ne suffit pas.
      // La lecture peut être en retard (consistance différée) : on réessaie
      // quelques secondes avec un backoff court avant de conclure à un échec.
      const { verifyOrderWithRetries, resolveSimulatedMisses } = await import(
        "@/lib/cod-verification.server"
      );

      const {
        verified: verifiedOrder,
        attempts,
        config: verifyConfig,
        errors: verifyErrors,
        abortedOnErrors,
      } = await verifyOrderWithRetries({
        expected: { id: order.id, name: order.name },
        correlationId,
        simulateMisses: resolveSimulatedMisses(),
        fetchOrder: async () => {
          const verified = await shopifyAdminRequest<{
            order?: { id: string; name: string } | null;
          }>(
            `query VerifyCodOrder($id: ID!) {
              order: node(id: $id) { ... on Order { id name } }
            }`,
            { id: order.id },
            { correlationId, operation: "VerifyCodOrder" },
          );
          return verified.order ?? null;
        },
      });

      if (!verifiedOrder) {
        serverLog("error", "cod_order.verification_failed", {
          correlationId,
          durationMs: Date.now() - startedAt,
          orderId: order.id,
          orderNumber: order.name,
          attempts,
          delaysMs: verifyConfig.delaysMs,
          configSource: verifyConfig.source,
          maxTotalWaitMs: verifyConfig.maxTotalWaitMs,
          errors: verifyErrors,
          maxErrors: verifyConfig.maxErrors,
          abortedOnErrors,
        });

        // Timeout de vérification : la commande existe peut-être déjà côté
        // Shopify. On la persiste en `verification_timeout` pour que le job de
        // réconciliation la reprenne et corrige son statut plus tard.
        try {
          const { upsertShopOrder, phoneLast4 } = await import("@/lib/shop-orders.server");
          await upsertShopOrder({
            shopifyOrderId: order.id,
            orderNumber: order.name,
            status: "verification_timeout",
            financialStatus: "pending",
            phoneLast4: phoneLast4(data.phone),
            correlationId,
          });
        } catch (persistError) {
          serverLog("error", "cod_order.verification_timeout_persist_failed", {
            correlationId,
            orderId: order.id,
            message: persistError instanceof Error ? persistError.message : String(persistError),
          });
        }

        throw new Error(`Commande Shopify non vérifiable (correlationId=${correlationId})`);
      }



      serverLog("info", "cod_order.created", {
        correlationId,
        durationMs: Date.now() - startedAt,
        orderId: order.id,
        orderNumber: order.name,
      });

      // Enregistrement local du statut + abonnement webhooks (idempotent).
      const { upsertShopOrder, phoneLast4 } = await import("@/lib/shop-orders.server");
      const { ensureWebhooksRegistered } = await import("@/lib/shopify-webhooks.server");
      await upsertShopOrder({
        shopifyOrderId: order.id,
        orderNumber: order.name,
        status: "created",
        financialStatus: "pending",
        phoneLast4: phoneLast4(data.phone),
        correlationId,
      });
      void ensureWebhooksRegistered();

      return { orderId: order.id, orderNumber: order.name, correlationId };

    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : String(error);
      serverLog("error", "cod_order.failed", {
        correlationId,
        durationMs: Date.now() - startedAt,
        message: rawMessage,
        stack: error instanceof Error ? error.stack : undefined,
      });
      const message = rawMessage.includes("correlationId=")
        ? rawMessage
        : `${rawMessage} (correlationId=${correlationId})`;
      throw new Error(message);
    }
  });

/** Consultation du statut d'une commande (numéro + téléphone), revérifiée en direct auprès de Shopify. */
export const getOrderStatus = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => orderStatusSchema.parse(input))
  .handler(async ({ data }) => {
    const { getVerifiedOrderStatus } = await import("@/lib/shop-order-status.server");
    const order = await getVerifiedOrderStatus(data.orderNumber, data.phone);
    return order ? { found: true as const, order } : { found: false as const };
  });


